import React, { FC, useContext, useState } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import { ScopeData } from 'models/ActivityDataEntryScopes/scopes.model';
import styles from '../activityDataEntry.module.scss';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { Scope2DesiredData } from 'models/ActivityDataScope2DesiredData/scope2DesiredData.model';
import { LocationData } from 'models/ActivityDataEntryScopes/locationData.model';
import { useParams } from 'react-router-dom';
import useFilters from 'shared/hooks/useFilters';
import { queryParamsList } from 'views/ActivityDataEntry';
import { transformItem } from 'shared/utils/convertScopeValues';
import SwitchToMonthlyModal from '../SwitchToMonthlyModal';
import SwitchAlertOfMonthlyDataModal from '../SwitchAlertOfMonthlyDataModal';
import LocationBasedFields from './LocationBasedField';
import MarketBasedFields from './MarketBasedFields';
import { scopeTwoValidationSchema } from './scope2ValidationSchema';
import { atleastOneRequired, marketBasedRECField } from 'constants/constants';
import { MarketBasedData } from 'models/ActivityDataEntryScopes/marketBased.model';

interface ScopeTwoFormComponentProps {
  formikRef?: React.RefObject<FormikProps<Scope2DesiredData>>;
  activeYear?: string;
  activeScope?: string;
  scope2Details?: Scope2DesiredData;
  locationScopeDetails?: LocationData;
  createScopeForLocation?: (
    locationId?: string,
    year?: string,
    scopeCategory?: string,
    payload?: ScopeData
  ) => Promise<void>;
  fetchYearForLocation?: (locationId?: string, year?: string) => Promise<void>;
  createDesiredScopeData?: (locationScopeDetails?: LocationData) => void;
  setIsEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEditView?: React.Dispatch<React.SetStateAction<boolean>>;
  isEditViewComponent?: boolean;
}

interface Scope2ErrorMessages {
  locationBased: { fuelValue: string }[];
  marketBased: { fuelValue: string }[];
}

export type OriginalData = {
  [key: string]: string; // Allow indexing with a string
};

const ScopeTwoFormComponent: FC<ScopeTwoFormComponentProps> = ({
  formikRef,
  activeYear,
  activeScope,
  scope2Details,
  createScopeForLocation,
  fetchYearForLocation,
  setIsEdit,
}) => {
  const { getParams, setParams } = useFilters();

  const [isSwitchMonthly, setIsSwitchMonthly] = useState(false);
  const [switchMonthlyItem, setSwitchMonthlyItem] = useState(new ScopeData());
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedSourceIndex, setSelectedSourceIndex] = useState('');
  const [isSwitchMonthlyAlert, setIsSwitchMonthlyAlert] = useState(false);
  const [initialMonthlyValues, setInitialMonthlyValues] = useState(
    new ScopeData()
  );

  const { isFirstFlow, isEditView } = getParams(queryParamsList);

  const { openTooltipModal } = useContext(HelpDeskTooltipContext);

  const { id: locationId } = useParams();

  const initialValues = {
    ...scope2Details,
    marketBased: scope2Details?.marketBased?.map(field => {
      if (field.fuelName === marketBasedRECField) {
        return {
          ...field,
           // Un comment after client give confirmation
          // electricityFromRec: field?.electricityFromRec
          //   ? field?.electricityFromRec
          //   : new MarketBasedData(),
        };
      } else {
        return { ...field };
      }
    }),
  };

  const handleSubmit = async (values: Scope2DesiredData) => {
    const { locationBased, marketBased } = values;
    const totalValues = [...(locationBased ?? []), ...(marketBased ?? [])];

    // Flatten and transform both arrays
    const locationBasedFlattened = totalValues?.map(transformItem);
    const desiredPayload = [...(locationBasedFlattened as ScopeData[])];
    await createScopeForLocation?.(
      locationId,
      activeYear,
      activeScope,
      desiredPayload as ScopeData
    );
    setIsEdit?.(false);
    setParams(
      isFirstFlow
        ? { activeTab: activeYear, isFirstFlow: true }
        : { activeTab: activeYear },
      undefined,
      true
    );
    await fetchYearForLocation?.(locationId, activeYear);
  };

  // Will add for new fields once its seeded in the DB
  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);

  const handleSwitch =
    (source?: ScopeData, key?: string, selectedIndex?: string) => () => {
      if (source?.fuelValue) {
        setIsSwitchMonthlyAlert?.(true);
      } else {
        setIsSwitchMonthly(true);
      }
      const values = formikRef?.current?.values;
      const sourceItem = values?.[key as keyof Scope2DesiredData];
      const selectedItem = sourceItem?.filter(item => source?.id === item?.id);
      const resultedValue = selectedItem?.[0];
      const desiredInitialValues = {
        ...resultedValue,
        scopeMonthlyData: !resultedValue?.isYearly
          ? resultedValue?.scopeMonthlyData
          : undefined,
      };
      setInitialMonthlyValues(desiredInitialValues);
      formikRef?.current?.setFieldValue(
        `${selectedIndex}`,
        desiredInitialValues
      );
      source && setSwitchMonthlyItem(source);
      key && setSelectedSource(key);
      selectedIndex && setSelectedSourceIndex(selectedIndex);
    };
  const handleMonthlyModalOpen =
    (source?: ScopeData, key?: string, selectedIndex?: string) => () => {
      source && setSwitchMonthlyItem(source);
      key && setSelectedSource(key);
      selectedIndex && setSelectedSourceIndex(selectedIndex);
      const values = formikRef?.current?.values;
      const sourceItem = values?.[key as keyof Scope2DesiredData];
      const selectedItem = sourceItem?.filter(item => source?.id === item?.id);
      const resultedValue = selectedItem?.[0];
      const desiredInitialValues = {
        ...resultedValue,
      };
      setInitialMonthlyValues(desiredInitialValues);
      setIsSwitchMonthly(true);
    };

  const handleModalSubmit = (values?: ScopeData) => {
    const totalValue = values?.scopeMonthlyData?.reduce((acc, item) => {
      return acc + (item?.value ? Number(item?.value) : 0);
    }, 0);
    formikRef?.current?.setFieldValue?.(`${selectedSourceIndex}`, {
      ...values,
      isYearly: false,
      fuelValue: Number.isInteger(totalValue)
        ? totalValue
        : totalValue?.toFixed(4).replace(/\.?0+$/, ''),
    });

    setIsSwitchMonthly(false);
  };

  const handleAlertSubmit = () => {
    const values = formikRef?.current?.values;
    const sourceItem = values?.[selectedSource as keyof Scope2DesiredData];
    const selectedItem = sourceItem?.filter(
      item => switchMonthlyItem?.id === item?.id
    );
    const resultedValue = selectedItem?.[0];
    if (resultedValue?.isYearly) {
      setIsSwitchMonthly(true);
    } else {
      const desiredInitialValues = {
        ...resultedValue,
        isYearly: !resultedValue?.isYearly,
        fuelValue: null,
        scopeMonthlyData: undefined,
      };
      setInitialMonthlyValues(desiredInitialValues);
      formikRef?.current?.setFieldValue(
        `${selectedSourceIndex}`,
        desiredInitialValues
      );
    }
    setIsSwitchMonthlyAlert?.(false);
  };
  const customValidate = (values: Scope2DesiredData) => {
    let errors: Scope2ErrorMessages = {
      locationBased:
        values?.locationBased?.map(() => ({ fuelValue: '' })) ?? [],
      marketBased: values?.marketBased?.map(() => ({ fuelValue: '' })) ?? [],
    };
    const isLocationBasedValues = values?.locationBased?.every(
      locationBased => locationBased?.fuelValue === null
    );
     // Un comment after client give confirmation
    // const isMarketBasedValues = values?.marketBased?.every(
    //   marketBased => marketBased?.fuelValue === null
    // );
    // if (isLocationBasedValues && isMarketBasedValues) {
      if (isLocationBasedValues) {
      values?.locationBased?.forEach((_, index) => {
        errors.locationBased[index].fuelValue = atleastOneRequired;
      });
      // Un comment after client give confirmation
      // values?.marketBased?.forEach((_, index) => {
      //   errors.marketBased[index].fuelValue = atleastOneRequired;
      // });
      return errors;
    }
    return {};
  };

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      enableReinitialize
      validationSchema={scopeTwoValidationSchema}
      validate={customValidate}
      onSubmit={handleSubmit}>
      <Form>
        <div className={styles.categoryContainer}>
          <p className={styles.stationaryCategoryTitle}>Location Based</p>
          <LocationBasedFields
            handleToolTipOpen={handleToolTipOpen}
            isEditView={isEditView}
            handleMonthlyModalOpen={handleMonthlyModalOpen}
            handleSwitch={handleSwitch}
          />
          <p className={styles.mobileSourceCategoryTitle}>Market Based (Feature coming soon!)</p>
          <MarketBasedFields
            handleToolTipOpen={handleToolTipOpen}
            isEditView={isEditView}
            handleMonthlyModalOpen={handleMonthlyModalOpen}
            handleSwitch={handleSwitch}
          />
        </div>
        <SwitchToMonthlyModal
          isModalOpen={isSwitchMonthly}
          setIsModalOpen={setIsSwitchMonthly}
          initialValues={initialMonthlyValues}
          handleSubmit={handleModalSubmit}
        />
        <SwitchAlertOfMonthlyDataModal
          isModalOpen={isSwitchMonthlyAlert}
          setIsModalOpen={setIsSwitchMonthlyAlert}
          handleSubmit={handleAlertSubmit}
          sourceValue={switchMonthlyItem}
        />
      </Form>
    </Formik>
  );
};

export default ScopeTwoFormComponent;